import { useEffect, useState, useMemo, memo } from "react"
import { db } from "../../../utils/firebase"

// ChartJS imports
import { Line } from "react-chartjs-2"
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend } from "chart.js"

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend)

// Helper function to get color based on year offset
const getColorForYear = (yearOffset) => {
	if (yearOffset === 0) return { border: "#e41f13", bg: "#e41f13" }

	// Different colors for each historical year, with reduced opacity for visibility
	const colors = [
		{ r: 59, g: 130, b: 246 }, // Blue
		{ r: 168, g: 85, b: 247 }, // Purple
		{ r: 34, g: 197, b: 94 }, // Green
		{ r: 251, g: 146, b: 60 }, // Orange
		{ r: 20, g: 184, b: 166 }, // Teal
	]

	const colorIndex = Math.min(yearOffset - 1, colors.length - 1)
	const color = colors[colorIndex]
	const opacity = 0.6 // Consistent opacity for all historical years

	return {
		border: `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`,
		bg: `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`,
	}
}

function SalesLineChart({ dateRanges, selectedYear, selectedMonth }) {
	const [bookingsByYear, setBookingsByYear] = useState({})
	const [loading, setLoading] = useState(true)

	// Fetch data for all date ranges
	useEffect(() => {
		const fetchAllData = async () => {
			setLoading(true)
			const dataByYear = {}

			for (const range of dateRanges) {
				const bookings = await db
					.collection("bookings")
					.where("created", ">", range.start)
					.where("created", "<", range.end)
					.get()
					.then((bookingDocs) => {
						const bookingsList = []
						let totalSales = 0
						bookingDocs.forEach((bookingDoc) => {
							const data = bookingDoc.data()
							bookingsList.push({ id: bookingDoc.id, ...data })
							totalSales += Math.round(data.margins?.customer_price) || 0
						})
						return { bookings: bookingsList, total: totalSales }
					})

				dataByYear[range.yearOffset] = {
					...bookings,
					year: range.year,
				}
			}

			setBookingsByYear(dataByYear)
			setLoading(false)
		}

		fetchAllData()
	}, [dateRanges])

	// Process sales data with memoization
	const processSales = useMemo(() => {
		return (data) => {
			const counts = {}

			data.forEach((booking) => {
				const date = new Date(booking.created.seconds * 1000)
				const normalizedDate = date.toLocaleDateString("en-GB", {
					day: "2-digit",
					month: "2-digit",
				})

				const customerPrice = booking.margins?.customer_price || 0
				const roundedPrice = Math.round(customerPrice)

				counts[normalizedDate] = (counts[normalizedDate] || 0) + roundedPrice
			})

			// Sort dates and calculate cumulative sales
			const sortedDates = Object.keys(counts).sort((a, b) => {
				const [dayA, monthA] = a.split("/").map(Number)
				const [dayB, monthB] = b.split("/").map(Number)
				return monthA === monthB ? dayA - dayB : monthA - monthB
			})

			const cumulativeSales = {}
			const dailySales = {}
			let runningTotal = 0
			sortedDates.forEach((date) => {
				runningTotal += counts[date]
				cumulativeSales[date] = runningTotal
				dailySales[date] = counts[date]
			})

			return { cumulativeSales, dailySales }
		}
	}, [])

	// Process data for all years with memoization
	const processedData = useMemo(() => {
		const processed = {}
		Object.keys(bookingsByYear).forEach((yearOffset) => {
			processed[yearOffset] = processSales(bookingsByYear[yearOffset].bookings)
		})
		return processed
	}, [bookingsByYear, processSales])

	// Get all unique dates with memoization
	const allDates = useMemo(() => {
		const datesSet = new Set()
		Object.values(processedData).forEach((yearData) => {
			Object.keys(yearData.cumulativeSales).forEach((date) => datesSet.add(date))
		})

		return Array.from(datesSet).sort((a, b) => {
			const [dayA, monthA] = a.split("/").map(Number)
			const [dayB, monthB] = b.split("/").map(Number)
			return monthA === monthB ? dayA - dayB : monthA - monthB
		})
	}, [processedData])

	// Check if we're viewing the current month/year
	const isCurrentMonth = useMemo(() => {
		const now = new Date()
		return selectedYear === now.getFullYear() && selectedMonth === now.getMonth()
	}, [selectedYear, selectedMonth])

	// Get current date for comparison (only used if viewing current month)
	const today = useMemo(() => {
		return new Date().toLocaleDateString("en-GB", {
			day: "2-digit",
			month: "2-digit",
		})
	}, [])

	// Generate datasets with memoization
	const chartData = useMemo(() => {
		const datasets = []

		// Generate a dataset for each year
		Object.keys(bookingsByYear)
			.sort((a, b) => parseInt(a) - parseInt(b))
			.forEach((yearOffset) => {
				const offset = parseInt(yearOffset)
				const yearData = processedData[offset]?.cumulativeSales || {}
				const color = getColorForYear(offset)
				const year = bookingsByYear[offset].year

				const data = allDates.map((date, index, dates) => {
					// For current year viewing current month, don't show future dates
					if (offset === 0 && isCurrentMonth && date > today) {
						return null
					}

					// If the current date has no value, carry forward the last value
					if (!yearData[date] || yearData[date] === 0) {
						for (let i = index - 1; i >= 0; i--) {
							const prevDate = dates[i]
							if (yearData[prevDate] && yearData[prevDate] !== 0) {
								return yearData[prevDate]
							}
						}
					}

					return yearData[date] || 0
				})

				datasets.push({
					label: offset === 0 ? `${year} (Current)` : `${year}`,
					data,
					borderColor: color.border,
					backgroundColor: color.bg,
					borderWidth: offset === 0 ? 3 : 2,
					pointRadius: offset === 0 ? 3 : 1,
					pointHoverRadius: offset === 0 ? 5 : 3,
					borderDash: offset === 0 ? [] : [5, 5],
					fill: false,
					tension: 0.1,
				})
			})

		return { labels: allDates, datasets }
	}, [bookingsByYear, processedData, allDates, today, isCurrentMonth])

	// Chart options with memoization
	const chartOptions = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			animations: {
				y: {
					easing: "easeOutQuart",
					duration: 1500,
				},
				x: {
					duration: 0,
				},
			},
			plugins: {
				legend: {
					position: "top",
					labels: {
						usePointStyle: true,
						padding: 15,
						font: {
							size: 12,
							weight: "500",
						},
					},
				},
				tooltip: {
					enabled: true,
					mode: "index",
					intersect: false,
					backgroundColor: "rgba(255, 255, 255, 0.95)",
					titleColor: "#111827",
					bodyColor: "#374151",
					borderColor: "#e5e7eb",
					borderWidth: 1,
					padding: 12,
					boxPadding: 6,
					usePointStyle: true,
					callbacks: {
						label: function (context) {
							const value = context.raw
							const date = context.label

							// Skip if value is null
							if (value === null || value === undefined) {
								return null
							}

							const formattedValue = new Intl.NumberFormat("en-GB", {
								style: "currency",
								currency: "GBP",
								maximumFractionDigits: 0,
							}).format(value)

							// Get the daily total for this date and dataset
							const yearOffset = context.datasetIndex
							const dailyData = processedData[yearOffset]?.dailySales || {}
							const dailyTotal = dailyData[date] || 0
							const formattedDaily = new Intl.NumberFormat("en-GB", {
								style: "currency",
								currency: "GBP",
								maximumFractionDigits: 0,
							}).format(dailyTotal)

							// Compare with current year if not current year
							if (context.datasetIndex !== 0 && chartData.datasets[0]) {
								const currentYearValue = chartData.datasets[0].data[context.dataIndex]

								// Skip comparison if current year value is null or undefined
								if (currentYearValue === null || currentYearValue === undefined) {
									return [`${context.dataset.label}: ${formattedValue}`, `Daily Total: ${formattedDaily}`]
								}

								const diff = currentYearValue - value
								const percentageDiff = value ? ((diff / value) * 100).toFixed(1) : "N/A"
								return [`${context.dataset.label}: ${formattedValue} (${diff >= 0 ? "+" : ""}${percentageDiff}% vs current)`, `Daily Total: ${formattedDaily}`]
							}

							return [`${context.dataset.label}: ${formattedValue}`, `Daily Total: ${formattedDaily}`]
						},
					},
				},
			},
			interaction: {
				mode: "index",
				intersect: false,
			},
			scales: {
				x: {
					title: {
						display: true,
						text: "Date (DD/MM)",
						font: { size: 12, weight: "600" },
						color: "#374151",
					},
					grid: {
						color: "rgba(229, 231, 235, 0.5)",
						drawBorder: false,
					},
					ticks: {
						maxRotation: 45,
						minRotation: 45,
						font: { size: 10 },
						color: "#6b7280",
					},
				},
				y: {
					title: {
						display: true,
						text: "Cumulative Sales (£)",
						font: { size: 12, weight: "600" },
						color: "#374151",
					},
					grid: {
						color: "rgba(229, 231, 235, 0.5)",
						drawBorder: false,
					},
					ticks: {
						font: { size: 10 },
						color: "#6b7280",
						callback: function (value) {
							return (
								"£" +
								new Intl.NumberFormat("en-GB", {
									notation: "compact",
									compactDisplay: "short",
								}).format(value)
							)
						},
					},
				},
			},
		}),
		[chartData]
	)

	if (loading) {
		return (
			<div>
				<h2>Sales Trends</h2>
				<p>Loading data...</p>
			</div>
		)
	}

	return (
		<div>
			<h2>Sales Trends</h2>
			{Object.keys(bookingsByYear)
				.sort((a, b) => parseInt(a) - parseInt(b))
				.map((yearOffset) => {
					const offset = parseInt(yearOffset)
					const data = bookingsByYear[offset]
					return (
						<p key={offset}>
							{offset === 0 ? "Current" : `${data.year}`}: £{data.total.toLocaleString()}
						</p>
					)
				})}
			<br />
			<div style={{ height: "600px", width: "100%" }}>
				<Line
					data={chartData}
					options={chartOptions}
				/>
			</div>
		</div>
	)
}

export default memo(SalesLineChart)
